import {Entity} from 'draft-js';
import {
	FILE_CHIP_CLASS,
	FILE_REF_ATTRIBUTE,
	isFileReference as isForecastFileReference,
	parsedMediaMetadata,
} from '../../../task-modal/inlineImages/fileReference';

const isJsonObject = jsonString => {
	try {
		var o = JSON.parse(jsonString);

		if (o && typeof o === 'object') {
			return true;
		}
	} catch (e) {}

	return false;
};

// The href attribute is preferred over the resolved `node.href` property so a relative or
// smart-link href is preserved verbatim; text nodes have no getAttribute, so fall back to .href.
const getHref = node => (node.getAttribute ? node.getAttribute('href') || node.href : node.href);

// Match by class-token membership, not exact string equality: Plate can add its own classes
// (selection, hover, etc.) to a node on blur, and an exact `===` would then miss the class and
// silently drop the entity (a card degraded to a plain LINK, or a mention lost entirely). This
// also keeps mention/inline-card/action-item detection consistent instead of mixing `===` and
// token checks. classList is undefined on text nodes, so fall back to the exact check there
// (where it is simply false).
const hasClass = (node, token) => (node.classList ? node.classList.contains(token) : node.className === token);

const getEntityId = node => {
	let entityId = undefined;
	const isMention = hasClass(node, 'slate-mention');
	const isActionItem = hasClass(node, 'slate-action_item');
	if (node instanceof HTMLAnchorElement) {
		const entityConfig = {};
		const isInlineCard = hasClass(node, 'slate-inline-card');
		if (isInlineCard) {
			// Jira smart-link (inline card) — keep it as its own entity type so the href is
			// preserved on the way back, matching the emit side in block.js (PIKA-542).
			// Write `url` too: the Jira sync builds ADF inlineCard attrs from this data and the
			// ADF schema names the key `url`, so writing href alone left a card that survived a
			// Forecast edit with no url for the outbound converter to send back.
			const cardHref = getHref(node);
			entityConfig.href = cardHref;
			entityConfig.url = cardHref;
			entityId = Entity.__create('JIRA_INLINECARD', 'IMMUTABLE', entityConfig);
		} else if (node.dataset && node.dataset.mention !== undefined) {
			entityConfig.url = node.href;
			entityConfig.text = node.innerHTML;
			entityConfig.value = node.dataset.value;
			entityId = Entity.__create('MENTION', 'IMMUTABLE', entityConfig);
		} else {
			entityConfig.url = getHref(node);
			entityConfig.title = node.innerHTML;
			entityConfig.targetOption = node.target;
			entityId = Entity.__create('LINK', 'MUTABLE', entityConfig);
		}
	} else if (isMention) {
		const mentionData = node.getAttribute('data-slate-value');
		const hasDataObject = isJsonObject(mentionData);
		const mentionDataObject = hasDataObject
			? JSON.parse(mentionData)
			: {
					personId: node.getAttribute('data-slate-value'),
					name: node.innerHTML,
			  };

		entityId = Entity.__create('MENTION', hasDataObject ? 'IMMUTABLE' : 'SEGMENTED', {
			mention: mentionDataObject,
		});
	} else if (isActionItem) {
		entityId = Entity.__create('ACTION_ITEM', 'SEGMENTED', {
			checked: node.querySelector('[role=checkbox]').getAttribute('data-state') === 'checked',
		});
	} else if (hasClass(node, FILE_CHIP_CLASS)) {
		// An inline reference to a file, sitting at the point in the sentence that
		// mentions it. Handled here rather than as an atomic block for exactly that
		// reason — a block would push it onto its own line and lose the position that
		// gives it meaning.
		//
		// The reference lives in a data attribute, never an href: the emit side cannot
		// use one because isSafeHref allowlists http/https/mailto, and bypassing that
		// guard on an anchor would let a javascript: URL through where the equivalent
		// bypass for an <img src> is harmless.
		//
		// Only our own references are accepted, so a hand-written or pasted span cannot
		// turn into a chip pointing at a third-party URL.
		const reference = node.getAttribute(FILE_REF_ATTRIBUTE);
		if (isForecastFileReference(reference)) {
			// IMMUTABLE, matching MENTION and JIRA_INLINECARD above and the inline void the
			// chip is on the design-system side. MUTABLE lets the label be retyped in the
			// legacy editor, but the chip's name is read off data-name on the other side of
			// the wire and its children are ignored — so an edited label is displayed as the
			// stored name and re-serialized over the edit on the next save. An edit that
			// cannot survive is better not offered.
			entityId = Entity.__create('FILE', 'IMMUTABLE', {
				src: reference,
				// The metadata parse is shared with the player: a player degrades to a chip, so
				// the two have to agree on what an unknown size looks like.
				...parsedMediaMetadata(node),
			});
		}
	}
	return entityId;
};

export default getEntityId;
